import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  TextField,
  Modal,
  ModalFooter,
  Selection,
  Checkbox,
} from '@folio/stripes-components';
import { FormattedMessage, useIntl } from 'react-intl';

const BatchEditVolumesModal = ({
  open,
  onClose,
  onConfirm,
  selectedVolumes = [],
  isProcessing = false,
}) => {
  const intl = useIntl();

  // Form states
  const [operation, setOperation] = useState('');
  const [newPrefix, setNewPrefix] = useState('');
  const [customPrefix, setCustomPrefix] = useState('');
  const [isCustomPrefix, setIsCustomPrefix] = useState(false);
  const [resetCopyNumbers, setResetCopyNumbers] = useState(true);

  // Predefined prefixes
  const prefixOptions = [
    { value: 'مج.', label: 'مج.' },
    { value: 'VOL.', label: 'VOL.' },
    { value: 'custom', label: 'Custom' },
  ];

  // Operation options
  const operationOptions = [
    {
      value: 'changePrefix',
      label: intl.formatMessage({
        id: 'ui-plugin-inventory-templates.batchEditVolumesModal.operation.changePrefix',
        defaultMessage: 'Change Volume Prefix'
      })
    },
    {
      value: 'resetNumbers',
      label: intl.formatMessage({
        id: 'ui-plugin-inventory-templates.batchEditVolumesModal.operation.resetNumbers',
        defaultMessage: 'Reset Copy Numbers'
      })
    },
  ];

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setOperation('');
      setNewPrefix('');
      setCustomPrefix('');
      setIsCustomPrefix(false);
      setResetCopyNumbers(true);
    }
  }, [open]);

  const handlePrefixChange = (value) => {
    if (value === 'custom') {
      setIsCustomPrefix(true);
      setNewPrefix('');
    } else {
      setIsCustomPrefix(false);
      setNewPrefix(value);
      setCustomPrefix('');
    }
  };

  const handleCustomPrefixChange = (e) => {
    const value = e.target.value;
    setCustomPrefix(value);
    setNewPrefix(value);
  };

  const handleConfirm = () => {
    const finalPrefix = isCustomPrefix ? customPrefix : newPrefix;

    const editData = {
      operation,
      prefix: finalPrefix,
      resetCopyNumbers,
      selectedVolumes,
    };

    onConfirm(editData);
  };

  const isValidOperation = operation !== '';
  const isValidPrefix = operation !== 'changePrefix' ||
    (operation === 'changePrefix' && newPrefix.trim() !== '');

  const isFormValid = isValidOperation && isValidPrefix;

  // Render footer for fixed footer
  const renderFooter = (
    <ModalFooter>
      <Button
        buttonStyle="primary"
        onClick={handleConfirm}
        disabled={!isFormValid || isProcessing}
        marginBottom0
      >
        {isProcessing ? (
          <FormattedMessage
            id="ui-plugin-inventory-templates.batchEditVolumesModal.processing"
            defaultMessage="Processing..."
          />
        ) : (
          <FormattedMessage
            id="ui-plugin-inventory-templates.batchEditVolumesModal.apply"
            defaultMessage="Apply Changes"
          />
        )}
      </Button>
      <Button onClick={onClose} marginBottom0>
        <FormattedMessage
          id="ui-plugin-inventory-templates.batchEditVolumesModal.cancel"
          defaultMessage="Cancel"
        />
      </Button>
    </ModalFooter>
  );

  return (
    <Modal
      open={open}
      footer={renderFooter}
      label={
        <FormattedMessage
          id="ui-plugin-inventory-templates.batchEditVolumesModal.label"
          defaultMessage="Edit Selected Volumes"
        />
      }
      onClose={onClose}
      size="small"
    >
      <div style={{ padding: '20px' }}>
        <p style={{ marginBottom: '16px' }}>
          <FormattedMessage
            id="ui-plugin-inventory-templates.batchEditVolumesModal.description"
            values={{
              volumeCount: selectedVolumes.length,
            }}
            defaultMessage="Edit {volumeCount} selected volumes:"
          />
        </p>

        {/* List of selected volumes */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontWeight: '600', marginBottom: '8px' }}>
            <FormattedMessage
              id="ui-plugin-inventory-templates.batchEditVolumesModal.selectedVolumes.title"
              defaultMessage="Selected volumes:"
            />
          </div>
          <div style={{
            padding: '8px',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            maxHeight: '100px',
            overflowY: 'auto'
          }}
          >
            {selectedVolumes.map((volume) => (
              <span
                key={volume}
                style={{
                  display: 'inline-block',
                  margin: '2px 4px',
                  padding: '2px 8px',
                  backgroundColor: '#e9ecef',
                  borderRadius: '12px',
                  fontSize: '12px'
                }}
              >
                {volume}
              </span>
            ))}
          </div>
        </div>

        {/* Operation Selection */}
        <div style={{ marginBottom: '16px' }}>
          <Selection
            label={
              <FormattedMessage
                id="ui-plugin-inventory-templates.batchEditVolumesModal.operation.label"
                defaultMessage="Operation"
              />
            }
            placeholder={intl.formatMessage({
              id: 'ui-plugin-inventory-templates.batchEditVolumesModal.operation.placeholder',
              defaultMessage: 'Select operation',
            })}
            dataOptions={operationOptions}
            value={operation}
            onChange={setOperation}
            required
          />
        </div>

        {/* Prefix Selection - only show if changePrefix operation is selected */}
        {operation === 'changePrefix' && (
          <>
            <div style={{ marginBottom: '16px' }}>
              <Selection
                label={
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.batchEditVolumesModal.prefix.label"
                    defaultMessage="New Volume Prefix"
                  />
                }
                placeholder={intl.formatMessage({
                  id: 'ui-plugin-inventory-templates.batchEditVolumesModal.prefix.placeholder',
                  defaultMessage: 'Select prefix',
                })}
                dataOptions={prefixOptions}
                value={isCustomPrefix ? 'custom' : newPrefix}
                onChange={handlePrefixChange}
                required
              />
            </div>

            {/* Custom Prefix Input */}
            {isCustomPrefix && (
              <div style={{ marginBottom: '16px' }}>
                <TextField
                  label={
                    <FormattedMessage
                      id="ui-plugin-inventory-templates.batchEditVolumesModal.customPrefix.label"
                      defaultMessage="Custom Prefix"
                    />
                  }
                  value={customPrefix}
                  onChange={handleCustomPrefixChange}
                  placeholder={intl.formatMessage({
                    id: 'ui-plugin-inventory-templates.batchEditVolumesModal.customPrefix.placeholder',
                    defaultMessage: 'Enter custom prefix',
                  })}
                  required
                />
              </div>
            )}
          </>
        )}

        {/* Reset Copy Numbers Option */}
        <div style={{ marginBottom: '16px' }}>
          <Checkbox
            checked={resetCopyNumbers}
            onChange={(e) => setResetCopyNumbers(e.target.checked)}
            label={
              <FormattedMessage
                id="ui-plugin-inventory-templates.batchEditVolumesModal.resetCopyNumbers.label"
                defaultMessage="Reset copy numbers to sequential order"
              />
            }
          />
        </div>

        {/* Preview/Info */}
        {operation && (
          <div
            style={{
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#e8f5e8',
              border: '1px solid #c3e6c3',
              borderRadius: '4px',
            }}
          >
            <div style={{ fontSize: '14px', color: '#2d5a2d' }}>
              {operation === 'changePrefix' && newPrefix && (
                <FormattedMessage
                  id="ui-plugin-inventory-templates.batchEditVolumesModal.preview.changePrefix"
                  values={{
                    prefix: newPrefix,
                    volumeCount: selectedVolumes.length,
                  }}
                  defaultMessage="Will change prefix to '{prefix}' for {volumeCount} volumes"
                />
              )}
              {operation === 'resetNumbers' && (
                <FormattedMessage
                  id="ui-plugin-inventory-templates.batchEditVolumesModal.preview.resetNumbers"
                  values={{
                    volumeCount: selectedVolumes.length,
                  }}
                  defaultMessage="Will reset copy numbers for items in {volumeCount} volumes"
                />
              )}
              {resetCopyNumbers && (
                <div style={{ marginTop: '4px', fontSize: '12px' }}>
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.batchEditVolumesModal.preview.copyNumbers"
                    defaultMessage="Copy numbers will be reset to sequential order"
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

    </Modal>
  );
};

BatchEditVolumesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  selectedVolumes: PropTypes.arrayOf(PropTypes.string),
  isProcessing: PropTypes.bool,
};

export default BatchEditVolumesModal;
